import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { VerifiedOutlined } from '@mui/icons-material';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import BusinessRounded from '@mui/icons-material/BusinessRounded';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import GroupAddOutlined from '@mui/icons-material/GroupAddOutlined';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import PersonPinOutlined from '@mui/icons-material/PersonPinOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LogoMenu from 'assets/logo-menu.svg?react';
import AssignmentLateOutlinedIcon from 'shared/components/icons/assignment-late-outlined-icon';
import IntegrationsIcon from 'shared/components/icons/integrations-icon';
import Routes from 'shared/enums/routes';

import UserMenu from 'features/menu/components/user-menu/user-menu';
import { MenuItemGroup } from 'features/menu/types/menu-item';
import useMenu from './hooks';
import styles from './menu.module.scss';

const Menu = (): React.JSX.Element => {
  const { goToRoute, isActive, authorizedMenuItems, showDivider } = useMenu();
  const { t } = useTranslation('menu');

  const menu: MenuItemGroup[] = [
    {
      key: 'keyUserMenuItems',
      menuItems: [
        {
          icon: <PersonPinOutlined />,
          titleTranslationKey: 'residentsSettings',
          route: Routes.Residents,
        },
        {
          icon: <GroupAddOutlined />,
          titleTranslationKey: 'TemporaryAccess',
          route: Routes.TemporaryAccess,
        },
        {
          icon: <HistoryRounded />,
          titleTranslationKey: 'history',
          route: Routes.History,
        },
        {
          icon: <DashboardOutlined />,
          titleTranslationKey: 'dashboard',
          route: Routes.Dashboard,
        },
      ],
    },
    {
      key: 'adminMenuItems',
      menuItems: [
        {
          icon: <BadgeOutlined />,
          titleTranslationKey: 'roles',
          route: Routes.Roles,
        },
        {
          icon: <AccountTreeOutlined />,
          titleTranslationKey: 'organisation',
          route: Routes.Organisation,
        },
        {
          icon: <SettingsOutlined />,
          titleTranslationKey: 'settings',
          route: Routes.Settings,
        },
        {
          icon: <IntegrationsIcon />,
          titleTranslationKey: 'integrations',
          route: Routes.Integrations,
        },
        {
          icon: <AssignmentLateOutlinedIcon />,
          titleTranslationKey: 'notifications',
          route: Routes.FaultsHistory,
        },
      ],
    },
    {
      key: 'tenantMenuItems',
      menuItems: [
        {
          icon: <BusinessRounded />,
          titleTranslationKey: 'tenants',
          route: Routes.Tenants,
        },
        {
          icon: <VerifiedOutlined />,
          titleTranslationKey: 'featureSubscriptions',
          route: Routes.FeatureSubscriptions,
        },
      ],
    },
  ];

  return (
    <>
      <div className={styles.menu}>
        <Drawer variant="permanent" classes={{ paper: styles.drawerPaper }}>
          <div className={styles.logoContainer}>
            <LogoMenu />
          </div>
          <nav className={styles.navigation}>
            {menu.map((group, index) => (
              <React.Fragment key={group.key}>
                {authorizedMenuItems(group.menuItems).map((item) => (
                  <Link to={item.route} key={item.route}>
                    <ListItem disablePadding className={styles.listItem}>
                      <ListItemButton
                        selected={isActive(item.route)}
                        onClick={() => goToRoute(item.route)}
                        className={styles.listItemButton}
                        classes={{
                          selected: styles.listItemButtonSelected,
                        }}
                      >
                        {item.icon && <ListItemIcon className={styles.listItemIcon}>{item.icon}</ListItemIcon>}
                        <ListItemText primary={t(item.titleTranslationKey)} className={styles.listItemText} />
                      </ListItemButton>
                    </ListItem>
                  </Link>
                ))}
                {showDivider(index, menu.length, group.menuItems) && <Divider className={styles.divider} />}
              </React.Fragment>
            ))}
          </nav>
        </Drawer>
      </div>
      <UserMenu />
    </>
  );
};

export default Menu;
